import { type PayloadAction, createSlice } from "@reduxjs/toolkit"
import { REHYDRATE } from "redux-persist/es/constants"

import { addPrimitiveCases } from "../../../redux/shared/add-primitive-cases.ts"
import type { RehydrateAction } from "../../../store.ts"
import { logout } from "../../LoginPage/redux/auth.thunks.ts"
import type { Event } from "../types/types.ts"
import {
  completeCalendarEvent,
  createCalendarEvent,
  deleteCalendarEvent,
  fetchEventDetails,
  fetchEvents,
  partialUpdateCalendarEvent,
  updateCalendarEvent,
  updateDraggableEvent,
  updateReminderStatus
} from "./calendar.thunks.ts"
import { type CalendarState } from "./calendar.types.ts"

// Initial State
const initialState: CalendarState = {
  addModalData: null,
  moreEventsData: null,
  selectedEvent: null,
  selectedEventRequestId: null,
  fetching: false,
  events: [],

  filters: {
    q: null,
    start: null,
    end: null,
    type: null,
    entity_type: null,
    participants: null
  },

  saveFetching: false,
  saveResult: null
}

// Slice
const calendarSlice = createSlice({
  name: "calendar",
  initialState,
  reducers: {
    calendarSetProp: (
      state,
      action: PayloadAction<{
        key: keyof CalendarState
        value: CalendarState[keyof CalendarState]
      }>
    ) => {
      const { key, value } = action.payload

      if (value && typeof value === "object" && !Array.isArray(value)) {
        state[key] = {
          ...(state[key] as Record<string, unknown>),
          ...value
        } as CalendarState[keyof CalendarState]
      } else {
        state[key] = value
      }
    },

    // A full replace of `selectedEvent`, untouched `fetching` — the same
    // shape `fetchEventDetails.fulfilled` below applies on a normal open,
    // for a caller (a background refresh) that has to write the result of
    // its own plain API call without going through that thunk (which would
    // also flip `fetching`, driving the fullscreen Spin) and without
    // `calendarSetProp`'s generic object-value path, which *merges* rather
    // than replaces and would leave a field a response omits holding its
    // stale value.
    replaceSelectedEvent: (state, action: PayloadAction<Event>) => {
      state.selectedEvent = action.payload
    }
  },
  extraReducers: (builder) => {
    // Only the chosen filters come back. Everything else in this slice
    // describes what was on screen a moment ago — the event behind the detail
    // modal above all — and a stored one reopened that modal on every later
    // visit to the Calendar, including after a crash the user never chose to
    // leave open. The persist transform already keeps them out of storage;
    // this is what protects a browser that stored one before that shipped.
    builder.addCase(REHYDRATE, (state, action: RehydrateAction) => {
      const stored = action?.payload?.calendar
      if (!stored) return state

      return {
        ...initialState,
        filters: stored.filters ?? initialState.filters
      }
    })

    addPrimitiveCases(builder, createCalendarEvent, "saveFetching")
    addPrimitiveCases(builder, updateCalendarEvent, "saveFetching")
    addPrimitiveCases(builder, partialUpdateCalendarEvent, "saveFetching")
    addPrimitiveCases(builder, updateReminderStatus, "saveFetching")
    addPrimitiveCases(builder, deleteCalendarEvent, "saveFetching")
    addPrimitiveCases(builder, completeCalendarEvent, "saveFetching")
    // addPrimitiveCases(builder, updateDraggableEvent, "saveFetching")

    builder
      // fetchEvents
      .addCase(fetchEvents.pending, (state) => {
        state.fetching = true
      })
      .addCase(fetchEvents.fulfilled, (state, action) => {
        state.fetching = false
        state.events = (action.payload?.results ||
          action.payload ||
          []) as Event[]
      })
      .addCase(fetchEvents.rejected, (state) => {
        state.fetching = false
      })

      // fetchEventDetails — two deep links followed in quick succession can
      // resolve out of order; only the latest request's result is applied,
      // so a slower, older fetch can't overwrite a newer one already open.
      .addCase(fetchEventDetails.pending, (state, action) => {
        state.fetching = true
        state.selectedEventRequestId = action.meta.requestId
      })
      .addCase(fetchEventDetails.fulfilled, (state, action) => {
        if (state.selectedEventRequestId !== action.meta.requestId) return
        state.fetching = false
        state.selectedEvent = action.payload
      })
      .addCase(fetchEventDetails.rejected, (state, action) => {
        if (state.selectedEventRequestId !== action.meta.requestId) return
        state.fetching = false
      })

      .addCase(logout.fulfilled, () => {
        return initialState
      })
  }
})

export const { calendarSetProp, replaceSelectedEvent } = calendarSlice.actions

export default calendarSlice
